import React, { useMemo, useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import ProductCard from '../product-card/product-card';
import ArrowIcon from '../utils/icon/icon-arrow.svg';

const ProductCarousel = ({ id: sectionId, items }) => {
  const sliderRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const isSelectedTab = (index) => index === activeIndex && items.length > 1;

  const activeProducts = useMemo(() => {
    return items[activeIndex]?.products;
  }, [activeIndex, items]);

  const activeItem = useMemo(() => {
    return items[activeIndex];
  }, [activeIndex, items]);

  if (!items.length) return null;

  useEffect(() => {
    if (sliderRef.current) {
      const [swiperWrapper, swiperNotification] = sliderRef.current.children;
      swiperWrapper.removeAttribute('aria-live');
      swiperNotification.removeAttribute('aria-live');
      swiperNotification.removeAttribute('aria-atomic');
    }
  }, []);

  return (
    <div
      className="mb-10 mt-3 px-3 lg:mt-10 lg:px-8"
      id={`product-carousel--${sectionId}`}
    >
      <div className="mb-6 flex items-center justify-between">
        <div className="flex flex-wrap items-center gap-6">
          {items.map((item, index) => (
            <button
              role="tab"
              aria-controls="product-carousel-tabpanel"
              {...(items.length > 1 && { 'aria-selected': isSelectedTab(index) })}
              className={clsx(
                'h5 relative cursor-pointer bg-transparent after:absolute after:bottom-[3px] after:left-0  after:h-[3px] after:w-full after:transition-[color,transform] [&.active]:after:translate-y-[3px] [&.active]:after:bg-brand-tangerine',
                {
                  active: isSelectedTab(index),
                },
              )}
              onClick={() => {
                setActiveIndex(index);
                sliderRef?.current?.swiper.slideTo(0);
              }}
              key={item.id}
            >
              {item.title}
            </button>
          ))}
        </div>
        <div
          className={clsx('hidden items-center gap-2 self-end', {
            'lg:hidden': activeProducts?.length <= 4,
            'lg:flex': activeProducts?.length > 4,
          })}
        >
          <button
            className="prev flex size-7.5 items-center justify-center rounded-full border border-default p-2 [&>svg]:h-2.5 [&>svg]:w-3 [&>svg]:rotate-180"
            ref={prevRef}
          >
            <ArrowIcon />
          </button>
          <button
            className="next flex size-7.5 items-center justify-center rounded-full border border-default p-2 [&>svg]:h-2.5 [&>svg]:w-3"
            ref={nextRef}
          >
            <ArrowIcon />
          </button>
        </div>
      </div>
      <div>
        {activeProducts?.length > 0 && (
          <div className="-mr-3 overflow-hidden pr-3 lg:mr-0 lg:pr-0">
            <Swiper
              className="!overflow-visible lg:!overflow-hidden"
              ref={sliderRef}
              id="product-carousel-tabpanel"
              modules={[Navigation]}
              slidesPerView="auto"
              spaceBetween={12}
              navigation={{
                prevEl: `#product-carousel--${sectionId} .prev`,
                nextEl: `#product-carousel--${sectionId} .next`,
                disabledClass: '!border-grey-medium',
              }}
              breakpoints={{
                1024: {
                  spaceBetween: 30,
                  slidesPerGroup: 2,
                  slidesPerGroupAuto: true,
                },
              }}
            >
              {activeProducts.map((product) => (
                <SwiperSlide
                  className="!w-[250px] lg:!w-[calc(25%-30px)]"
                  key={product.id}
                >
                  <ProductCard
                    key={product.id}
                    product={product}
                    placement="carousel"
                    inputPrefix={sectionId}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}
        {activeItem?.ctaText && activeItem?.ctaUrl && (
          <div className="text-center">
            <a
              className="button button--primary mt-9"
              href={activeItem?.ctaUrl}
            >
              {activeItem?.ctaText}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCarousel;
